'use client';

/* 地址选择组件-自提地址列表 */
// React
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
// 三方库/框架
import { useTranslation } from '@/i18n/client';
import to from 'await-to-js';
import dynamic from 'next/dynamic';
// 公共方法
import { JDILogin } from '@/lib/jdiClientUtils';
import { initAddress } from '@/utils/addressUtils';
import { getJDISiteKeys } from '@/utils/multiSite';
import {
  ADDRESS_CATEGORY_VALUES,
  ADDRESS_EVENT_NAMES,
  addressBusinessLogger,
} from '@/utils/logger/instance/addressBusinessLogger';
// 接口
import {
  getRecommendPickUpList,
  getSavedPickUpList,
  modifyPickUp,
  placeDetail,
} from '@/apis/address/address.api';
import { Address, GeolocationLocation } from '@/apis/address/address.interface';
// 组件
import UKLoadingIcon, { CIRCLE_COLOR_MAP } from '@/components/UKLoadingIcon';
import UKToast from '@/components/UKToast';
import AddressSearch from '@/components/address/AddressSearch';
import PickupDetail from '@/components/address/PickupDetail';
import SiteChangeConfirmDialog from '@/components/countrySelect/SiteChangeConfirmDialog';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
} from '@mui/material';
// 资源
import AddressEmptyIcon from '@/assets/icons/address/AddressEmptyIcon.svg';
import FrozenIcon from '@/assets/icons/address/FrozenIcon.svg';
import ListViewIcon from '@/assets/icons/address/ListViewIcon.svg';
import MapViewIcon from '@/assets/icons/address/MapViewIcon.svg';
import PickupPointLocationIcon from '@/assets/icons/address/PickupPointLocationIcon.svg';
import PickupPointOpenTimeIcon from '@/assets/icons/address/PickupPointOpenTimeIcon.svg';
import SavedIcon from '@/assets/icons/address/SavedIcon.svg';
import UnSavedIcon from '@/assets/icons/address/UnSavedIcon.svg';
// 枚举
import { ADDRESS_VERIFY_TYPE, PICK_MODIFY_TYPE } from '@/constants/address';
// 样式
import styles from './index.module.scss';

const GoogleMapComponent = dynamic(() => import('@/components/address/GoogleMap'), { ssr: false });

interface PickupListProps {
  defaultPickupTypeTab?: 'recommended' | 'saved';
  availablePickupTypes?: ('recommended' | 'saved')[];
  defaultViewType?: 'listView' | 'mapView';
  selectAddressId?: string | number; // 默认的地址id
  isFrigNeed?: boolean; // 是否需要冷藏冷冻
  extMap?: Record<string, any>; // 查询扩展参数
  onApply: (addressInfo: Address | null) => void; // 确认按钮点击事件
}

export interface PickupListRef {
  refreshAddressList: () => Promise<void>; // 刷新列表
  getSelectAddressInfo: () => Promise<Address>; // 获取选择的地址信息
  clearSelectAddress: () => void; // 清除当前勾选内容
}

const PickupList = forwardRef<PickupListRef, PickupListProps>((props, ref) => {
  const {
    defaultPickupTypeTab = 'recommended',
    availablePickupTypes,
    defaultViewType = 'listView',
    selectAddressId,
    isFrigNeed,
    extMap = {},
    onApply,
  } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectAddress, setSelectAddress] = useState<Address | null>(null); // 选中的地址信息
  const [recommendedAddressList, setRecommendedAddressList] = useState<Address[]>([]); // 推荐自提点列表
  const [savedAddressList, setSavedAddressList] = useState<Address[]>([]); // 收藏自提点列表(可用)
  const [savedUnavailableAddressList, setSavedUnavailableAddressList] = useState<Address[]>([]); // 收藏自提点列表(不可用)
  const [savedOtherSitePickUpList, setSavedOtherSitePickUpList] = useState<Address[]>([]); // 跨站点自提点列表

  const [availableTypes, setAvailableTypes] = useState<('recommended' | 'saved')[]>([
    'recommended',
    'saved',
  ]);
  const [activeTab, setActiveTab] = useState<'recommended' | 'saved'>(defaultPickupTypeTab);
  const [activeViewType, setActiveViewType] = useState<'listView' | 'mapView'>(defaultViewType);
  const [savingInProgress, setSavingInProgress] = useState<Record<number | string, boolean>>({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedPickupDetail, setSelectedPickupDetail] = useState<Address | null>(null);

  const [isLogin, setIsLogin] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number }>({
    lat: 51.508275,
    lng: -0.076246,
  });
  const [currentAddressInfo, setCurrentAddressInfo] = useState<Address | null>(null); // 位置对应的地址信息

  const [showSiteChangeDialog, setShowSiteChangeDialog] = useState(false); // 控制跨站点确认弹窗显示
  const [otherSiteAddressInfo, setOtherSiteAddressInfo] = useState<Address | null>(null);

  // 刷新列表
  useImperativeHandle(ref, () => ({
    refreshAddressList,
    getSelectAddressInfo,
    clearSelectAddress,
  }));

  // 获取地址列表数据
  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const validTypes = (availablePickupTypes || ['recommended', 'saved']).filter(
      (type) => type === 'recommended' || type === 'saved',
    ) as ('recommended' | 'saved')[];

    const types = validTypes.length > 0 ? validTypes : ['recommended' as const];
    setAvailableTypes(types);

    const initialType =
      defaultPickupTypeTab && types.includes(defaultPickupTypeTab as 'recommended' | 'saved')
        ? (defaultPickupTypeTab as 'recommended' | 'saved')
        : types[0];
    setActiveTab(initialType);
  }, [defaultPickupTypeTab, availablePickupTypes]);

  useEffect(() => {
    if (
      currentAddressInfo &&
      typeof currentAddressInfo.latitude === 'number' &&
      typeof currentAddressInfo.longitude === 'number'
    ) {
      const { latitude, longitude } = currentAddressInfo;
      setCurrentLocation({ lat: latitude, lng: longitude });
    }
  }, [currentAddressInfo]);

  useEffect(() => {
    refreshAddressList();
  }, [activeTab, currentAddressInfo, selectAddressId, isFrigNeed, JSON.stringify(extMap)]);

  // 检查选中的地址是否在当前页面展示的列表数据中
  const isSelectAddressInCurrentList = useMemo(() => {
    if (!selectAddress) {
      return false;
    }

    if (activeTab === 'recommended') {
      return recommendedAddressList?.some(
        (addr) => addr.pickUpSiteId === selectAddress.pickUpSiteId,
      );
    } else {
      return savedAddressList?.some((addr) => addr.pickUpSiteId === selectAddress.pickUpSiteId);
    }
  }, [selectAddress, recommendedAddressList, savedAddressList, activeTab]);

  const refreshAddressList = () => {
    if (activeTab === 'recommended') {
      return fetchRecommendedAddressList();
    } else {
      return fetchSavedAddressList();
    }
  };

  const clearSelectAddress = () => {
    setSelectAddress(null);
  };

  const handleTabChange = (tab: 'recommended' | 'saved') => {
    setActiveTab(tab);
  };

  const handleViewTypeChange = (type: 'listView' | 'mapView') => {
    setActiveViewType(type);
  };

  const init = async () => {
    // 获取登录状态
    const [loginErr, loginInfo] = await to<{ isLoggedIn: boolean }>(
      JDILogin.getInstance().getUserInfo(),
    );
    const isLoggedIn = loginErr ? false : loginInfo?.isLoggedIn;
    setIsLogin(isLoggedIn);

    const [, locationData] = await to<Address | null | undefined>(initAddress());
    if (locationData) {
      setCurrentAddressInfo(locationData);
    }
  };

  const getSelectAddressInfo = () => {
    return new Promise<Address>((resolve, reject) => {
      if (!selectAddress) {
        reject(t('address.noAddressSelected'));
      } else if (!selectAddress.postCode) {
        reject(t('address.noPostCode'));
      } else {
        resolve(selectAddress);
      }
    });
  };

  // 推荐站点地址列表
  const fetchRecommendedAddressList = async () => {
    if (!currentAddressInfo) return; // 没有位置信息不查询自提
    setLoading(true);

    const params: any = {
      countryCode: currentAddressInfo?.countryCode || getJDISiteKeys()?.['JDI-Country'],
      latitude: currentAddressInfo?.latitude,
      longitude: currentAddressInfo?.longitude,
      postCode: currentAddressInfo?.postCode,
      ...(isFrigNeed ? { isFrigNeed: true } : {}),
      ...extMap,
    };

    const [err, result] = await to(getRecommendPickUpList(params));

    if (err) {
      setRecommendedAddressList([]);
      setLoading(false);
      return;
    }

    let list = (result?.data as Address[]) || [];
    let foundAddress: Address | undefined;

    // 优先使用传入的selectAddressId 其次使用缓存中的addressId
    const addressIdToMatch = selectAddressId ?? currentAddressInfo?.pickUpSiteId;

    const index = list.findIndex((addr) => addr.pickUpSiteId === addressIdToMatch);
    if (index !== -1) {
      foundAddress = list[index];
      const filterList = list.filter((addr) => addr.pickUpSiteId !== addressIdToMatch);
      list = [foundAddress, ...filterList];
    }

    setLoading(false);

    // 设置地址列表数据
    setRecommendedAddressList(list);

    if (!addressIdToMatch || activeTab !== 'recommended') {
      // 没有地址id 则不处理勾选
      return;
    }

    if (foundAddress) {
      setSelectAddress(foundAddress);
    }

    if (result?.code !== '200') {
      // 地址列表查询异常上报
      addressBusinessLogger({
        eventName: ADDRESS_EVENT_NAMES.PICKUP_LIST_QUERY_ERROR,
        category: {
          module: ADDRESS_CATEGORY_VALUES.MODULE.ADDRESS_SELECT,
          action: ADDRESS_CATEGORY_VALUES.ACTION.REQUEST,
          result: ADDRESS_CATEGORY_VALUES.RESULT.ERROR,
        },
        message: {
          response: JSON.stringify(result),
          error: JSON.stringify(err),
        },
      });
    }
  };

  // 收藏站点地址列表
  const fetchSavedAddressList = async () => {
    setLoading(true);

    const params: any = {
      countryCode: currentAddressInfo?.countryCode || getJDISiteKeys()?.['JDI-Country'],
      latitude: currentAddressInfo?.latitude,
      longitude: currentAddressInfo?.longitude,
      ...(isFrigNeed ? { isFrigNeed: true } : {}),
      ...extMap,
    };

    const [err, result] = await to(getSavedPickUpList(params));

    if (err) {
      setSavedAddressList([]);
      setLoading(false);
      return;
    }

    const { data } = result as any;
    const list = (data?.validPickUps as Address[]) || [];
    const otherSiteList = ((data?.otherCountryPickUps as Address[]) || [])?.map((item) => {
      return {
        ...item,
        otherSite: true, // 跨站点地址
      };
    });
    const unavailableList = ((data?.unValidPickUps as Address[]) || [])?.map((item) => {
      return {
        ...item,
        disableSelection: true, // 不可选
      };
    });

    // 优先使用传入的selectAddressId 其次使用缓存中的addressId
    const addressIdToMatch = selectAddressId ?? currentAddressInfo?.pickUpSiteId;

    // 查找并处理匹配的地址
    const { foundAddress, updatedLists } = findAndProcessAddress(addressIdToMatch, [
      list,
      unavailableList,
      otherSiteList,
    ]);

    setLoading(false);

    if (foundAddress) {
      setSelectAddress(foundAddress);
    }

    // 设置地址列表数据
    setSavedAddressList(updatedLists[0]);
    setSavedUnavailableAddressList(updatedLists[1]);
    setSavedOtherSitePickUpList(updatedLists[2]);

    if (result?.code !== '200') {
      // 地址列表查询异常上报
      addressBusinessLogger({
        eventName: ADDRESS_EVENT_NAMES.PICKUP_LIST_QUERY_ERROR,
        category: {
          module: ADDRESS_CATEGORY_VALUES.MODULE.ADDRESS_SELECT,
          action: ADDRESS_CATEGORY_VALUES.ACTION.REQUEST,
          result: ADDRESS_CATEGORY_VALUES.RESULT.ERROR,
        },
        message: {
          response: JSON.stringify(result),
          error: JSON.stringify(err),
        },
      });
    }
  };

  // 查找并处理收藏站点列表
  const findAndProcessAddress = (
    addressIdToMatch: string | number | undefined,
    lists: (Address[] | null)[],
  ): {
    foundAddress: Address | undefined;
    updatedLists: Address[][];
  } => {
    // 如果没有要匹配的ID，直接返回原始列表
    if (!addressIdToMatch) {
      return {
        foundAddress: undefined,
        updatedLists: lists.map((list) => list || []) as Address[][],
      };
    }

    let foundAddress: Address | undefined;
    let foundInList: number = -1;

    // 在可用列表+不可用列表+其他站点列表的集合中查找匹配的地址
    for (let i = 0; i < lists.length; i++) {
      const currentList = lists[i];
      if (!currentList?.length) continue;

      const index = currentList.findIndex((addr) => addr.pickUpSiteId === addressIdToMatch);
      if (index !== -1) {
        foundAddress = currentList[index];
        foundInList = i;
        break;
      }
    }

    const updatedLists = lists.map((list) => (list ? [...list] : [])) as Address[][];

    // 如果找到了匹配的地址，将其移到对应列表的顶部
    if (foundAddress && foundInList !== -1) {
      updatedLists[foundInList] = updatedLists[foundInList].filter(
        (addr) => addr.pickUpSiteId !== addressIdToMatch,
      );
      updatedLists[foundInList] = [foundAddress, ...updatedLists[foundInList]];
    }

    return { foundAddress, updatedLists };
  };

  const handleSelectSearchResult = async (placeId: string) => {
    if (!placeId) return;

    const [, result] = await to(placeDetail({ placeId }));
    result?.data && setCurrentAddressInfo(result.data);
  };

  // 位置变更
  const handleLocationChange = (
    position: GeolocationLocation,
    addressInfo: Address | null | undefined,
  ) => {
    if (!addressInfo?.verifyResult) {
      UKToast.show({
        content: `${t('address.locationUnableToDeliver')}` as any,
        icon: 'warning',
      });
      return;
    }
    addressInfo && setCurrentAddressInfo(addressInfo);
  };

  const handlePickupSelectChange = (pickupInfo: Address) => {
    if (pickupInfo?.otherSite) {
      setOtherSiteAddressInfo(pickupInfo);
      // 跨站点二次确认
      setShowSiteChangeDialog(true);
    } else {
      setSelectAddress(pickupInfo);
    }
  };

  const handleChangeSavedStatus = async (e: React.MouseEvent, address: Address, index: number) => {
    if (savingInProgress[index]) {
      return;
    }

    setSavingInProgress((prev) => ({
      ...prev,
      [index]: true,
    }));

    try {
      const modifyType =
        activeTab === 'saved'
          ? PICK_MODIFY_TYPE.UN_SAVE
          : address.isSaved
            ? PICK_MODIFY_TYPE.UN_SAVE
            : PICK_MODIFY_TYPE.SAVE;

      const params = {
        pickUpId: address.pickUpSiteId,
        modifyType,
      };

      const [err, result] = await to(modifyPickUp(params));

      if (!err && result?.success) {
        if (activeTab === 'recommended') {
          setRecommendedAddressList((prevList) =>
            prevList.map((item) =>
              item.pickUpSiteId === address.pickUpSiteId
                ? { ...item, isSaved: !item.isSaved }
                : item,
            ),
          );
        } else {
          setSavedAddressList((prevList) =>
            prevList.filter((item) => item.pickUpSiteId !== address.pickUpSiteId),
          );
          setSavedUnavailableAddressList((prevList) =>
            prevList.filter((item) => item.pickUpSiteId !== address.pickUpSiteId),
          );
          setSavedOtherSitePickUpList((prevList) =>
            prevList.filter((item) => item.pickUpSiteId !== address.pickUpSiteId),
          );
        }
        const content: any =
          modifyType === PICK_MODIFY_TYPE.SAVE
            ? t('address.savePickupSuccess')
            : t('address.unSavePickupSuccess');
        UKToast.show({
          content,
          icon: 'success',
        });
      } else {
        const content: any =
          modifyType === PICK_MODIFY_TYPE.SAVE
            ? t('address.savePickupFail')
            : t('address.unSavePickupFail');
        UKToast.show({
          content,
          icon: 'error',
        });
      }
    } finally {
      setSavingInProgress((prev) => ({
        ...prev,
        [index]: false,
      }));
    }
  };

  // 判断是否是整个列表的最后一项
  const isLastItemInAllLists = (
    address: Address,
    mode: 'recommended' | 'savedNormal' | 'savedUnavailable' | 'otherSite',
  ) => {
    if (mode === 'recommended') {
      return (
        recommendedAddressList[recommendedAddressList.length - 1]?.pickUpSiteId ===
        address?.pickUpSiteId
      );
    } else {
      let lastNonEmptyList: Address[] | null = null;
      // 实际渲染顺序从后向前检查
      if (savedOtherSitePickUpList && savedOtherSitePickUpList.length > 0) {
        lastNonEmptyList = savedOtherSitePickUpList;
      } else if (savedUnavailableAddressList && savedUnavailableAddressList.length > 0) {
        lastNonEmptyList = savedUnavailableAddressList;
      } else if (savedAddressList && savedAddressList.length > 0) {
        lastNonEmptyList = savedAddressList;
      }
      if (!lastNonEmptyList) return false;

      return lastNonEmptyList[lastNonEmptyList.length - 1].addressId === address.addressId;
    }
  };

  const handleApply = () => {
    getSelectAddressInfo()
      .then((res) => {
        onApply?.(res);
      })
      .catch((err) => {
        UKToast.show({
          content: err,
          icon: 'error',
        });
      });
  };

  const renderAddressItem = (
    address: Address,
    index: number,
    mode: 'recommended' | 'savedNormal' | 'savedUnavailable' | 'otherSite',
    inMapView: boolean = false,
  ) => {
    const isUnavailable = mode === 'savedUnavailable' || mode === 'otherSite';
    const isLastItem = isLastItemInAllLists(address, mode);
    const disableSelection = address?.disableSelection;
    const isSaved =
      mode === 'savedNormal' || mode === 'savedUnavailable' || mode === 'otherSite'
        ? true
        : !!address?.isSaved;
    return (
      <React.Fragment key={index}>
        <div
          className={`${styles.pickupItem} ${isUnavailable ? styles.unavailable : ''} ${
            inMapView ? styles.inMapView : ''
          }`}
          onClick={() => !disableSelection && handlePickupSelectChange(address)}
        >
          <FormControlLabel
            value={address.pickUpSiteId?.toString()}
            control={
              <Radio
                disableRipple
                checked={selectAddress?.pickUpSiteId === address.pickUpSiteId}
                color='secondary'
                sx={{
                  color: '#E0E1E5',
                }}
              />
            }
            label={
              <div
                className={`${styles.pickupItem} ${inMapView ? styles.inMapView : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className={styles.pickupBetweenLine}>
                  <div className={styles.pickupName}>
                    {address.siteName}
                    {address?.isLastUse && address?.lastUseStr ? (
                      <span className={styles.lastUsedTag}>{address.lastUseStr}</span>
                    ) : null}
                  </div>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      handleChangeSavedStatus(e, address, index);
                    }}
                    style={{ opacity: savingInProgress[index] ? 0.5 : 1 }}
                  >
                    {isSaved ? <SavedIcon /> : <UnSavedIcon />}
                  </div>
                </div>

                <div className={styles.pickupStartLine}>
                  <div className={styles.pickupAddress}>{address?.address}</div>
                </div>

                <div className={styles.pickupStartLine}>
                  {address.distance !== undefined && address.distance !== null && (
                    <div className={styles.pickupDistance}>
                      <PickupPointLocationIcon />
                      {`${address.distance} km`}
                    </div>
                  )}

                  {address.openTimeStr && (
                    <div className={styles.pickupTime}>
                      <PickupPointOpenTimeIcon />
                      {address.openTimeStr}
                    </div>
                  )}
                </div>

                <div className={styles.pickupBetweenLine}>
                  <div className={styles.pickFrozen}>
                    {address?.frozenFlag && (
                      <>
                        <FrozenIcon />
                        <span>{address?.frozenInfo?.text || t('address.frozenSupport')}</span>
                      </>
                    )}
                  </div>

                  <div
                    className={styles.detailsBtn}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedPickupDetail(address);
                      setDetailDialogOpen(true);
                    }}
                  >
                    {t('address.lib.details')}
                  </div>
                </div>
              </div>
            }
            className={`${styles.radioControl} ${inMapView ? styles.mapViewRadioControl : ''}`}
          />
        </div>

        {!isLastItem && !inMapView && <Divider className={styles.divider} />}
      </React.Fragment>
    );
  };

  const renderAddressList = (mode: 'recommended' | 'saved') => {
    return (
      <>
        {mode === 'recommended' && (
          <>
            {recommendedAddressList && recommendedAddressList.length > 0 ? (
              <div className={styles.pickupListContent}>
                {recommendedAddressList.map((address, index) =>
                  renderAddressItem(address, index, 'recommended'),
                )}
              </div>
            ) : (
              <div className={styles.emptyList}>
                <AddressEmptyIcon />
                <div>{t('address.pickupEmptyText')}</div>
              </div>
            )}
          </>
        )}

        {mode === 'saved' && (
          <>
            {savedAddressList && savedAddressList.length > 0 ? (
              <div className={styles.pickupListContent}>
                {savedAddressList.map((address, index) =>
                  renderAddressItem(address, index, 'savedNormal'),
                )}
              </div>
            ) : null}

            {savedUnavailableAddressList && savedUnavailableAddressList.length > 0 ? (
              <div className={styles.pickupListContent}>
                {savedUnavailableAddressList.map((address, index) =>
                  renderAddressItem(address, index, 'savedUnavailable'),
                )}
              </div>
            ) : null}

            {savedOtherSitePickUpList && savedOtherSitePickUpList.length > 0 ? (
              <div className={styles.pickupListContent}>
                <div className={`${styles.sectionTitle} ${styles.disabled}`}>
                  {t('address.otherRegionsAddresses')}
                </div>
                {savedOtherSitePickUpList.map((address, index) =>
                  renderAddressItem(address, index, 'otherSite'),
                )}
              </div>
            ) : null}

            {(!savedAddressList || savedAddressList.length === 0) &&
              (!savedUnavailableAddressList || savedUnavailableAddressList.length === 0) &&
              (!savedOtherSitePickUpList || savedOtherSitePickUpList.length === 0) && (
                <div className={styles.emptyList}>
                  <AddressEmptyIcon />
                  <div>
                    {mode === 'saved'
                      ? t('common.pageStatus.nothing')
                      : t('address.pickupEmptyText')}
                  </div>
                </div>
              )}
          </>
        )}
      </>
    );
  };

  return (
    <div className={styles.pickupWrap}>
      <div className={styles.topBarWrap}>
        <div className={styles.searchBar}>
          <AddressSearch onSearch={handleSelectSearchResult} />

          {activeViewType === 'mapView' && (
            <div className={styles.viewChangeBtn} onClick={() => handleViewTypeChange('listView')}>
              <ListViewIcon />
              <div>{t('address.listView')}</div>
            </div>
          )}

          {activeViewType === 'listView' && (
            <div className={styles.viewChangeBtn} onClick={() => handleViewTypeChange('mapView')}>
              <MapViewIcon />
              <div>{t('address.mapView')}</div>
            </div>
          )}
        </div>

        {availableTypes?.length > 1 && (
          <div className={styles.tabContainer}>
            {availableTypes.includes('recommended') && (
              <div
                className={`${styles.tabItem} ${activeTab === 'recommended' ? styles.active : ''}`}
                onClick={() => handleTabChange('recommended')}
              >
                {t('address.recommended')}
              </div>
            )}
            {availableTypes.includes('saved') && (
              <div
                className={`${styles.tabItem} ${activeTab === 'saved' ? styles.active : ''}`}
                onClick={() => handleTabChange('saved')}
              >
                {t('address.saved')}
              </div>
            )}
          </div>
        )}
      </div>

      {activeViewType === 'listView' && (
        <>
          {loading ? (
            <div className={styles.loadingContainer}>
              <UKLoadingIcon type={CIRCLE_COLOR_MAP.BLACK} />
            </div>
          ) : (
            <div className={styles.pickupList}>{renderAddressList(activeTab)}</div>
          )}

          {/* 底部按钮 */}
          <div className={styles.bottomBarWrap}>
            <Button className={styles.applyButton} onClick={handleApply}>
              {t('address.lib.apply')}
            </Button>
          </div>
        </>
      )}

      {activeViewType === 'mapView' && (
        <div className={styles.mapContainer}>
          <GoogleMapComponent
            className={styles.maps}
            currentLocation={currentLocation}
            verifyType={ADDRESS_VERIFY_TYPE.SITE_VERIFY} // 站点维度校验
            onLocationChange={handleLocationChange}
            hasBottomOverlay={isSelectAddressInCurrentList} // 当有选中的自提点时 调整地图布局
            locationPoints={
              // 自提站点
              (activeTab === 'recommended' ? recommendedAddressList : savedAddressList)
                .filter(
                  (addr) => typeof addr.latitude === 'number' && typeof addr.longitude === 'number',
                )
                .map((addr) => ({
                  lat: addr.latitude as number,
                  lng: addr.longitude as number,
                  id: addr.pickUpSiteId,
                }))
            }
            onLocationPointClick={(point, index) => {
              // 选中自提点
              const addressList =
                activeTab === 'recommended' ? recommendedAddressList : savedAddressList;
              const clickedAddress = addressList.find(
                (addr) => addr.latitude === point.lat && addr.longitude === point.lng,
              );

              if (clickedAddress) {
                handlePickupSelectChange(clickedAddress);
              }
            }}
          />

          {/* 选中的自提点信息 */}
          {isSelectAddressInCurrentList && (
            <div className={styles.mapPickupInfoContainer}>
              <div className={styles.mapPickupInfo}>
                {renderAddressItem(
                  selectAddress as Address,
                  -1,
                  activeTab === 'recommended' ? 'recommended' : 'savedNormal',
                  true,
                )}

                <Button className={styles.applyButton} onClick={handleApply}>
                  {t('address.lib.apply')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 自提点详情弹窗 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        aria-labelledby='pickup-detail-dialog-title'
        sx={{
          '& .MuiDialog-paper': {
            width: '800px',
            maxWidth: '800px',
            maxHeight: '600px',
            position: 'relative',
          },
        }}
      >
        <IconButton
          aria-label='close'
          onClick={() => setDetailDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          id='pickup-detail-dialog-title'
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            paddingBottom: 0,
          }}
        >
          {t('address.pickupDetails')}
        </DialogTitle>
        <PickupDetail pickupInfo={selectedPickupDetail} />
      </Dialog>

      {/* 跨站点确认弹窗 */}
      <SiteChangeConfirmDialog
        open={showSiteChangeDialog}
        toCountryCode={otherSiteAddressInfo?.countryCode || ''}
        toCountryName={
          otherSiteAddressInfo?.countryNameMultiLan || otherSiteAddressInfo?.countryName || ''
        }
        setDialogOpen={setShowSiteChangeDialog}
      />
    </div>
  );
});

PickupList.displayName = 'PickupList';

export default PickupList;
